import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { rupiah, shortDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Wallet, TrendingUp, FileText, Percent, Plane } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tint = "teal" }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 text-2xl font-extrabold text-slate-900 mono">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tint === "teal" ? "bg-[#f0f2e6] text-[#556b2f]" : "bg-amber-50 text-amber-700"}`}>
        <Icon size={20} />
      </div>
    </div>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/customers/${id}`).then((r) => setC(r.data)); }, [id]);
  if (!c) return <div className="text-slate-400">Memuat detail...</div>;

  return (
    <div className="space-y-6" data-testid="customer-detail">
      <div className="flex items-center gap-3">
        <Link to="/customers"><Button variant="ghost" size="sm"><ArrowLeft size={16} className="mr-1"/> Kembali</Button></Link>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#556b2f] font-bold">Detail Customer</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">{c.name}</h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">{c.phone || "-"}  ·  {c.email || "-"}</p>
        {c.notes && <p className="text-sm text-slate-500 mt-2 italic">{c.notes}</p>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={FileText} label="Total Transaksi" value={c.total_transactions || 0} />
        <Stat icon={Wallet} label="Total Belanja" value={rupiah(c.total_spent || 0)} />
        <Stat icon={TrendingUp} label="Total Terbayar" value={rupiah(c.total_paid || 0)} />
        <Stat icon={Percent} label="Net Margin" value={rupiah(c.gross_margin || 0)} />
      </div>

      <Card>
        <CardHeader><CardTitle>Airline Preferensi</CardTitle></CardHeader>
        <CardContent>
          {(c.airlines || []).length === 0 ? <p className="text-sm text-slate-400">Belum ada.</p> : (
            <div className="flex flex-wrap gap-2">
              {c.airlines.map((a, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-[#f0f2e6] text-[#556b2f] text-xs font-semibold flex items-center gap-1">
                  <Plane size={12}/> {a}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Riwayat Invoice</CardTitle></CardHeader>
        <CardContent>
          {(c.invoices || []).length === 0 ? <p className="text-sm text-slate-400">Belum ada invoice.</p> : (
            <ul className="divide-y divide-slate-100" data-testid="cust-invoices">
              {c.invoices.map((i) => (
                <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/invoices/${i.id}`} className="mono font-semibold text-slate-900 hover:text-[#556b2f]">{i.invoice_number}</Link>
                    <div className="text-xs text-slate-500 truncate">{shortDate(i.invoice_date)} · {i.airline || "-"} · {i.route || "-"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono font-bold">{rupiah(i.total)}</div>
                    <div className={`text-xs font-semibold ${i.payment_status === "Paid" ? "text-[#556b2f]" : "text-amber-700"}`}>{i.payment_status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
